/**
 * DynBlockManager — dynamic block properties of the current selection.
 *
 * Exposed to the UI as `Facade.dynBlocks`, following the capability convention of
 * the other managers (canXxxSelected / getSelectedXxx / setSelectedXxx) so Vue
 * components never touch ODA objects directly.
 *
 * Everything degrades to "no dynamic blocks" when the loaded DrawingWeb build is
 * older than 27.7 — see isDynBlockApiAvailable().
 */
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import { selectionService } from "../selection";
import {
  isDynBlockApiAvailable,
  isDynamicBlockRef,
  listDynBlockProperties,
  getDynamicBlockName,
  setDynBlockProperty,
  getPanelRows,
  type DynBlockPropertyInfo,
  type DynBlockPanelRow,
} from "../blocks/dynamic";
import { SetDynBlockPropertyCommand } from "../commands/SetDynBlockPropertyCommand";
import { findEntityByHandle, getEntityHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { dynPickBus, type DynBlockPickRequest } from "../blocks/dynPickBus";
import { Logger } from "@/logger";

export class DynBlockManager {
  constructor(private readonly viewer: DRDrawingViewer) {}

  /**
   * Subscribe to "a handle wants the user to pick a value" requests.
   * The canvas handles cannot render a list themselves — they emit here and the
   * Vue layer shows the picker. Passing null closes it.
   * @returns unsubscribe function
   */
  onPickRequest(
    handler: (request: DynBlockPickRequest | null) => void,
  ): () => void {
    return dynPickBus.subscribe(handler);
  }

  /** Close the picker (the handles open it themselves through the bus). */
  closePicker(): void {
    dynPickBus.emit(null);
  }

  /**
   * Apply a value to a property of a block designated by handle rather than by
   * selection — used by the picker, whose target was captured when it opened.
   */
  setPropertyByHandle(
    blockHandle: string,
    propertyName: string,
    value: string,
  ): boolean {
    const entityId = findEntityByHandle(
      this.viewer.module,
      this.viewer.database,
      blockHandle,
    );
    if (!entityId || entityId.isNull()) return false;

    const change = setDynBlockProperty(
      this.viewer.module,
      entityId,
      propertyName,
      value,
    );
    deleteObj(entityId);
    if (!change) return false;

    Logger.info(`DynBlockManager : ${propertyName} = ${value}`);
    trackShapeChange("dyn_block_param_changed", { property: propertyName });
    this.viewer.executeCommand(
      new SetDynBlockPropertyCommand(this.viewer, {
        blockHandle,
        changes: [change],
      }),
    );
    this.viewer.forceRegen();
    selectionService.updateGrid();
    return true;
  }

  /**
   * Live half of a continuous edit started from a canvas grip (the angle dial).
   * Targets the block by handle rather than by selection, like setPropertyByHandle:
   * the editor captured its target when it opened.
   */
  previewPropertyByHandle(
    blockHandle: string,
    propertyName: string,
    value: string,
  ): boolean {
    const entityId = findEntityByHandle(
      this.viewer.module,
      this.viewer.database,
      blockHandle,
    );
    if (!entityId || entityId.isNull()) return false;

    const change = setDynBlockProperty(
      this.viewer.module,
      entityId,
      propertyName,
      value,
    );
    deleteObj(entityId);
    if (!change) return false;

    this.viewer.forceRegen();
    selectionService.updateGrid();
    return true;
  }

  /** Record a finished grip-driven edit as a single undo step. */
  commitPropertyByHandle(
    blockHandle: string,
    propertyName: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (oldValue === newValue) return;
    Logger.info(`DynBlockManager : ${propertyName} ${oldValue} -> ${newValue}`);
    trackShapeChange("dyn_block_param_changed", { property: propertyName });
    this.viewer.executeCommand(
      new SetDynBlockPropertyCommand(this.viewer, {
        blockHandle,
        changes: [{ propertyName, oldValue, newValue }],
      }),
    );
  }

  /** True when the engine supports dynamic block properties at all (>= 27.7). */
  get isSupported(): boolean {
    return isDynBlockApiAvailable(this.viewer.module);
  }

  /**
   * True when exactly one dynamic block reference is selected.
   * Drives the visibility of every dynamic-block control.
   */
  canEditSelected(): boolean {
    if (!this.isSupported) return false;
    const ids = selectionService.getAll();
    if (ids.length !== 1) return false;
    return isDynamicBlockRef(this.viewer.module, ids[0]);
  }

  /** Name of the selected dynamic block definition (e.g. "Fleche PR"), or null. */
  getSelectedBlockName(): string | null {
    if (!this.canEditSelected()) return null;
    const ids = selectionService.getAll();
    return getDynamicBlockName(this.viewer.module, ids[0]);
  }

  /** All dynamic properties of the selected block, hidden and read-only ones included. */
  getSelectedProperties(): DynBlockPropertyInfo[] {
    if (!this.canEditSelected()) return [];
    const ids = selectionService.getAll();
    return listDynBlockProperties(this.viewer.module, ids[0]);
  }

  /**
   * Rows to show in the panel, in display order (visibility first).
   *
   * Covers every editable parameter — value lists, flips, point parameters and free
   * numeric scalars — each tagged with the glyph of its canvas grip, if it has one.
   */
  getSelectedPanelRows(): DynBlockPanelRow[] {
    return getPanelRows(this.getSelectedProperties());
  }

  /**
   * Apply a new value to one dynamic property of the selected block.
   * Returns false when nothing changed (unknown property, same value, read-only).
   */
  setSelectedProperty(propertyName: string, value: string): boolean {
    if (!this.canEditSelected()) return false;
    const ids = selectionService.getAll();

    const change = setDynBlockProperty(
      this.viewer.module,
      ids[0],
      propertyName,
      value,
    );
    if (!change) return false;

    Logger.info(`DynBlockManager : ${propertyName} = ${value}`);

    trackShapeChange("dyn_block_param_changed", { property: propertyName });
    const command = new SetDynBlockPropertyCommand(this.viewer, {
      blockHandle: getEntityHandle(ids[0]),
      changes: [change],
    });
    this.viewer.executeCommand(command);

    // setValue() re-evaluates the block into a new anonymous representation:
    // a regen is required, and the selection grid must follow the new extents.
    this.viewer.forceRegen();
    selectionService.updateGrid();
    return true;
  }

  /**
   * Apply a value without recording anything — the live half of a continuous edit
   * (the angle dial). The gesture is recorded once, on release, by
   * commitSelectedProperty(); recording every intermediate value would flood the
   * history with steps the user never chose.
   */
  previewSelectedProperty(propertyName: string, value: string): boolean {
    if (!this.canEditSelected()) return false;
    const ids = selectionService.getAll();

    const change = setDynBlockProperty(
      this.viewer.module,
      ids[0],
      propertyName,
      value,
    );
    if (!change) return false;

    this.viewer.forceRegen();
    selectionService.updateGrid();
    return true;
  }

  /**
   * Record a finished continuous edit as a single undo step. The value is already
   * applied — previewSelectedProperty() did that — so this only writes history.
   */
  commitSelectedProperty(
    propertyName: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (!this.canEditSelected() || oldValue === newValue) return;
    const ids = selectionService.getAll();

    Logger.info(`DynBlockManager : ${propertyName} ${oldValue} -> ${newValue}`);
    trackShapeChange("dyn_block_param_changed", { property: propertyName });
    this.viewer.executeCommand(
      new SetDynBlockPropertyCommand(this.viewer, {
        blockHandle: getEntityHandle(ids[0]),
        changes: [{ propertyName, oldValue, newValue }],
      }),
    );
  }

  /**
   * Toggle a two-state property (flip states) to its other allowed value.
   * Returns false when the property is not a two-value list.
   */
  toggleSelectedProperty(propertyName: string): boolean {
    const prop = this.getSelectedProperties().find(
      (p) => p.name === propertyName,
    );
    if (!prop || prop.readOnly || prop.allowedValues.length !== 2) return false;
    const next =
      prop.allowedValues.find((value) => value !== prop.value) ??
      prop.allowedValues[0];
    return this.setSelectedProperty(propertyName, next);
  }
}
